// =============================================================================
// pdfEncryption.js, Phase 42 helpers for password-protected PDF support.
//
// We detect encryption client-side so we can prompt the user BEFORE pushing
// the file across the wire (faster + calmer UX). Detection is a cheap byte
// scan, no PDF library required.
//
// Decryption is done server-side via pikepdf (see /api/import/parse-pdf*).
// The password travels over TLS in the same request body and is held only
// in memory for the lifetime of one request, never logged or persisted.
// =============================================================================

/**
 * Detect whether a PDF file is password-protected.
 *
 * Strategy: read up to the last ~32 KB of the file (where the PDF trailer
 * lives) and look for the `/Encrypt` key. This works for ~99% of real-world
 * encrypted PDFs, including all bank statements we've seen. False positives
 * are theoretically possible if the literal string appears in an unencrypted
 * stream, but practically negligible.
 *
 * Falls back to scanning the whole file if it's small (<64 KB).
 *
 * @param {File|Blob} file
 * @returns {Promise<boolean>}
 */
export async function isPdfEncrypted(file) {
  try {
    if (!file || typeof file.slice !== 'function') return false;
    const size = file.size || 0;
    // PDF trailer is at the end. Read last 32 KB (or whole file if smaller).
    const tailStart = Math.max(0, size - 32 * 1024);
    const tailBuf = await file.slice(tailStart, size).arrayBuffer();
    const tail = new TextDecoder('latin1').decode(tailBuf);
    if (/\/Encrypt\b/.test(tail)) return true;
    // Some PDFs put trailer in the middle of the file when linearized;
    // scan the first 32 KB too as a belt-and-suspenders check.
    if (size > 32 * 1024) {
      const headBuf = await file.slice(0, 32 * 1024).arrayBuffer();
      const head = new TextDecoder('latin1').decode(headBuf);
      if (/\/Encrypt\b/.test(head)) return true;
    }
    return false;
  } catch (e) {
    // If we can't read the file for any reason, don't gate uploads  
    // let the server tell us via 401 if it really is encrypted.
    return false;
  }
}
